import type { PosType } from '../../../../../gateways/breadstack/posConnectionApis'
import type { WeeklyStoreHours } from '../../detail/tabs/hours/types'
import type { PaymentsFormValues } from '../../detail/tabs/payments/shared/validationSchema'
import type { DeliveryProviderSubmit } from '../DeliveryProviderDialog'
import type { MailOrderProviderSubmit } from '../MailOrderProviderDialog'

// Step keys in wizard order. POS was added in front of the legacy 5-step flow
// (design mock `AddLocationModal`, STEP_KEYS) and is skippable.
export type StepKey =
  | 'pos'
  | 'name'
  | 'address'
  | 'hours'
  | 'fulfillment'
  | 'payments'

export const STEP_ORDER: StepKey[] = [
  'pos',
  'name',
  'address',
  'hours',
  'fulfillment',
  'payments',
]

export const STEP_LABELS: Record<StepKey, string> = {
  pos: 'POS',
  name: 'Name',
  address: 'Address',
  hours: 'Hours',
  fulfillment: 'Fulfillment',
  payments: 'Payments',
}

// Titles mirror the legacy wizard exactly (plus the new POS step). See
// src/pages/Settings/Locations/components/AddLocation/index.tsx around line 235.
export const STEP_TITLES: Record<StepKey, string> = {
  pos: 'Connect POS',
  name: 'Add location name',
  address: 'Edit location address',
  hours: 'Edit store hours',
  fulfillment: 'Edit fulfillment options',
  payments: 'Edit payment options',
}

// Fields the POS step can prefill on the Name + Address steps.
export interface PosPrefill {
  name?: string
  phone?: string
  address1?: string
  address2?: string
  city?: string
  state?: string
  country?: string
  postalCode?: string
}

// The POS location picked on the POS step. Linked to the draft warehouse via
// link-pos right after the Address step creates it.
export interface PosSelection {
  provider: PosType
  posLocationId: string
  companyId?: number | string
  locationName: string
  // Greenline multi POS — decided on the POS step (see pos/multiCompany.ts).
  oneStoreManyCompanies?: boolean
  prefill: PosPrefill
}

// Single-slot per day model — matches the detail-tab StoreHoursEditor. Values
// are 12h strings (e.g. "9:00 AM"); a `null` entry means the day is closed.
export type HoursMap = WeeklyStoreHours

export interface AddLocationDraft {
  // POS step — null when skipped
  pos: PosSelection | null
  // Name step
  name: string
  // Address step
  phone: string
  address1: string
  address2: string
  city: string
  state: string
  country: string
  postalCode: string
  license: string
  locationVisible: boolean
  // Hours step
  hours: HoursMap
  // Fulfillment step
  localPickup: boolean
  curbsidePickup: boolean
  driveThroughPickup: boolean
  enableDelivery: boolean
  enableMailOrder: boolean
  // Fulfillment step — warehouse-level provider linkage. These mirror the detail tabs'
  // `location.mailOrderLinked` / `location.deliveryLinked`. The wizard's toggle
  // gating uses these to decide whether to open the provider dialog: a fresh
  // draft starts empty, so first toggle-on ALWAYS opens the dialog. After a
  // successful connect+link the linked key is recorded here and subsequent
  // toggles skip the dialog (parity with `MailOrderTab.handleToggleMailOrder`
  // and `DeliveryTab.handleToggleDelivery`).
  mailOrderLinked: string
  deliveryLinked: string
  // Payments step. Same nested shape as the detail-tab `PaymentsForm` so
  // the wizard can reuse `CashSection`, `CreditDebitCardSection`, and
  // `InteracETransferSection` without any data conversion.
  payments: PaymentsFormValues['payments']
}

// Per-step Formik values. Each step submits a typed payload; the wizard merges
// it back into the accumulating draft and either advances or triggers the
// step's backend mutation.

export interface NameStepValues {
  name: string
}

export interface AddressStepValues {
  phone: string
  address1: string
  address2: string
  city: string
  state: string
  country: string
  postalCode: string
  license: string
  locationVisible: boolean
}

export interface HoursStepValues {
  hours: HoursMap
}

export interface FulfillmentStepValues {
  localPickup: boolean
  curbsidePickup: boolean
  driveThroughPickup: boolean
  enableDelivery: boolean
  enableMailOrder: boolean
  // Deferred provider configuration captured by the in-step provider dialogs.
  // The dialog's Save click sets these; no API fires until the wizard's
  // Save & Next consumes them.
  pendingMailProvider?: MailOrderProviderSubmit | null
  pendingDeliveryProvider?: DeliveryProviderSubmit | null
  // Resolved linkage keys handed up by FulfillmentStep.onSubmit after running
  // the connect mutations. The orchestrator merges these into the SAME
  // fulfillment PATCH as the enable flags so `delivery_linked` /
  // `mail_order_linked` are persisted in a single API call.
  mailOrderLinked?: string
  deliveryLinked?: string
  deliveryApiKey?: string
}

// PaymentsStep submits the same shape the detail-tab `PaymentsForm` submits.
export type PaymentsStepValues = PaymentsFormValues

const emptyMethod = () => ({
  enabled: false,
  enabled_pickup: false,
  enabled_curbside: false,
  enabled_drive_through: false,
  enabled_delivery: false,
  enabled_mail_order: false,
})

export const DEFAULT_DRAFT: AddLocationDraft = {
  pos: null,
  name: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  country: 'CA',
  postalCode: '',
  license: '',
  locationVisible: false,
  // Default to Mon–Fri 9:00 AM → 5:00 PM, Sat/Sun closed — matches the detail
  // editor's "open day" preset.
  hours: {
    monday: { from: '9:00 AM', to: '5:00 PM' },
    tuesday: { from: '9:00 AM', to: '5:00 PM' },
    wednesday: { from: '9:00 AM', to: '5:00 PM' },
    thursday: { from: '9:00 AM', to: '5:00 PM' },
    friday: { from: '9:00 AM', to: '5:00 PM' },
    saturday: null,
    sunday: null,
  },
  localPickup: false,
  curbsidePickup: false,
  driveThroughPickup: false,
  enableDelivery: false,
  enableMailOrder: false,
  mailOrderLinked: '',
  deliveryLinked: '',
  payments: {
    cash: emptyMethod(),
    credit_debit_card: emptyMethod(),
    interac_e_transfer: {
      ...emptyMethod(),
      recipient_email: '',
      security_question: '',
      security_answer: '',
      additional_notes: '',
      description: '',
    },
  },
}
